import { useState, useEffect } from "react";
import { supabase } from "./supabase";
import { ChargeModal } from "./StripePayment";
import { addCartToWallet } from "./PackagesManager";
import { loadPaymentSettings, cardFeeFor, cardFeePct, feeLabel } from "./paymentSettings";
import { settleVisitAppointment } from "./visitAppointment";
import { receiptEmailFields } from "./receipt";

async function sendReceipt(payment, client, setStatus) {
  try {
    setStatus && setStatus("sending");
    // ★ Rebuild the itemized breakdown from what the payment stored (same as
    //   the client-profile resend) — this resend used to send only the total.
    const { data, error } = await supabase.functions.invoke("send-receipt", {
      body: {
        to_email: client?.email,
        to_name: client?.name,
        ...receiptEmailFields(payment),
        service: payment.description || "Payment",
        provider: payment.provider || "",
        date: new Date(payment.created_at || Date.now()).toLocaleDateString("en-US", {month:"long",day:"numeric",year:"numeric"}),
        payment_method: (payment.payment_method || "card").replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()),
        last4: payment.card_last4 || "",
        transaction_id: payment.stripe_payment_intent_id || payment.external_reference || payment.id,
        notes: payment.notes || "",
      }
    });
    if (error) throw error;
    if (!data?.success) throw new Error(data?.error || "Unknown error");
    setStatus && setStatus("sent");
    alert("✅ Receipt sent to " + client?.email);
    return true;
  } catch (e) {
    setStatus && setStatus("error");
    alert("❌ Failed to send receipt: " + e.message);
    return false;
  }
}

const C = {
  bg:"#f8f5f0", surface:"#ffffff", card:"#ffffff", border:"#e8e4df",
  borderGold:"rgba(180,140,60,0.3)", goldBg:"rgba(180,140,60,0.08)",
  gold:"#b48c3c", goldD:"#8a6f2e",
  green:"#2e8b57", greenL:"rgba(46,139,87,0.12)",
  red:"#c0392b", redL:"rgba(192,57,43,0.10)",
  blue:"#2563eb", blueL:"rgba(37,99,235,0.10)",
  purple:"#7c3aed",
  text:"#1a1a2e", muted:"#6b7280", dim:"#9ca3af", light:"#f3f4f6",
};
const F = "'Montserrat',sans-serif";
const S = "'Playfair Display','Cormorant Garamond',Georgia,serif";

const PAYMENT_METHODS = [
  { id:"card", label:"Credit Card", icon:"💳", color:C.blue },
  { id:"cash", label:"Cash", icon:"💵", color:C.green },
  { id:"check", label:"Check", icon:"📝", color:C.muted },
  { id:"cherry", label:"Cherry", icon:"🍒", color:C.red },
  { id:"carecredit", label:"CareCredit", icon:"🏥", color:C.blue },
  { id:"affirm", label:"Affirm", icon:"💸", color:C.purple },
  { id:"zelle", label:"Zelle", icon:"📱", color:C.purple },
  { id:"venmo", label:"Venmo", icon:"💰", color:C.blue },
  { id:"other", label:"Other", icon:"📋", color:C.muted },
];

// fontSize 16 prevents iOS auto-zoom on input focus (a chronic UX
// frustration for staff using iPhones). padding bumped to 12 to maintain
// touch-target size.
const inp = {width:"100%",padding:"12px 14px",background:C.light,border:"1px solid "+C.border,borderRadius:8,fontSize:16,color:C.text,fontFamily:F,outline:"none",boxSizing:"border-box"};

export function RecordPaymentModal({ client, appointmentId, defaultAmount, description, startSplit, onClose, onComplete }) {
  // cart_items_v2 - new cart-based version
  const [services, setServices] = useState([]);
  const [cart, setCart] = useState([]); // [{id, name, price, qty}]
  const [serviceSearch, setServiceSearch] = useState("");
  const [showServicePicker, setShowServicePicker] = useState(false);
  const [discountType, setDiscountType] = useState("amount");
  const [discountValue, setDiscountValue] = useState(0);
  const [customAmount, setCustomAmount] = useState(defaultAmount || 0);
  const [useCustom, setUseCustom] = useState(!!defaultAmount && defaultAmount > 0);
  const [method, setMethod] = useState("cash");
  const [desc, setDesc] = useState(description || "");
  const [checkNumber, setCheckNumber] = useState("");
  const [externalRef, setExternalRef] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [showStripeCharge, setShowStripeCharge] = useState(false);
  const [paySettings, setPaySettings] = useState(null);
  // Split payment — pay one sale across several tenders (e.g. $3000 cash +
  // $2000 Cherry, or half cash / half card). Each line is {method, amount};
  // they must add up to the total.
  const [splitMode, setSplitMode] = useState(!!startSplit);
  const [splitLines, setSplitLines] = useState([{ method: "cash", amount: "" }, { method: "card", amount: "" }]);
  // When a split includes a card line we charge it on the terminal (via
  // ChargeModal) FIRST, then record the other tenders. pendingSplit holds the
  // non-card lines to record after the card succeeds.
  const [pendingSplit, setPendingSplit] = useState(null);
  const [showSplitCard, setShowSplitCard] = useState(false);

  // Tab in the service picker — services vs retail products. The DB has an
  // `item_type` column ('service' or 'product') so we can split the list.
  const [pickerTab, setPickerTab] = useState("service");

  useEffect(() => {
    (async () => {
      const { data } = await supabase.from("services").select("id,name,category,price,item_type").eq("active", true).order("category").order("name");
      setServices(data || []);
    })();
    loadPaymentSettings().then(setPaySettings);
  }, []);

  const cartSubtotal = cart.reduce((s,i) => s + (+i.price * +i.qty), 0);
  const subtotal = useCustom ? +customAmount : cartSubtotal;
  const discountAmount = discountType === "percent" ? subtotal * (+discountValue / 100) : +discountValue;
  const total = Math.max(0, subtotal - discountAmount);
  const methodLabel = (id) => (PAYMENT_METHODS.find(m => m.id === id)?.label || id);
  const splitAllocated = splitLines.reduce((s, l) => s + (parseFloat(l.amount) || 0), 0);
  const splitRemaining = +(total - splitAllocated).toFixed(2);
  const splitBalanced = Math.abs(splitRemaining) < 0.01;
  const splitHasCard = splitLines.some(l => l.method === "card" && (parseFloat(l.amount) || 0) > 0);

  const filteredServices = services.filter(s => {
    // Apply tab first (defaults to service for old rows missing item_type)
    const isProduct = s.item_type === "product";
    if (pickerTab === "product" && !isProduct) return false;
    if (pickerTab === "service" && isProduct) return false;
    if (!serviceSearch) return true;
    const q = serviceSearch.toLowerCase();
    return s.name.toLowerCase().includes(q) || (s.category || "").toLowerCase().includes(q);
  });
  const serviceCount = services.filter(s => s.item_type !== "product").length;
  const productCount = services.filter(s => s.item_type === "product").length;

  const addToCart = (svc) => {
    const existing = cart.find(i => i.id === svc.id);
    if (existing) setCart(cart.map(i => i.id === svc.id ? {...i, qty: i.qty + 1} : i));
    else setCart([...cart, { id: svc.id, name: svc.name, price: +svc.price || 0, qty: 1 }]);
    setShowServicePicker(false);
    setServiceSearch("");
  };

  const updateQty = (id, qty) => {
    if (qty <= 0) setCart(cart.filter(i => i.id !== id));
    else setCart(cart.map(i => i.id === id ? {...i, qty} : i));
  };

  const removeFromCart = (id) => setCart(cart.filter(i => i.id !== id));

  // Edit the per-unit price of a cart line (e.g. drop IV from $250 to $200).
  // This flows straight into the wallet: the saved payment's metadata.cart_items
  // carries this price, and the auto-wallet trigger uses it as the unit_value —
  // so the wallet value matches exactly what was charged.
  const updatePrice = (id, price) => setCart(cart.map(i => i.id === id ? { ...i, price: Math.max(0, price) } : i));

  const itemsList = cart.map(i => `${i.qty}× ${i.name}`).join(", ");

  const save = async () => {
    // Friendlier validation — if the cart is empty AND custom-amount mode is off,
    // explain what to do instead of just saying "must be ≥ $0.50".
    if (total < 0.5) {
      if (!useCustom && cart.length === 0) {
        setError("Pick a service (click + Add Service) or switch to 💰 Custom Amount.");
      } else if (useCustom && (!customAmount || +customAmount <= 0)) {
        setError("Enter a custom amount of at least $0.50.");
      } else {
        setError("Total must be at least $0.50");
      }
      return;
    }

    if (method === "card") {
      if (!client?.id) { setError("Credit card payments require an existing client."); return; }
      setShowStripeCharge(true);
      return;
    }

    // ★ Wrap the whole non-card save in try/catch so any throw is visible —
    //   the previous flow could throw on `user.id` if auth was missing and
    //   the user would see nothing happen (modal stayed open, no error).
    try {
      await saveCashFlow();
    } catch (e) {
      console.error("[record-payment] save crashed:", e);
      setError("Save failed: " + (e?.message || e));
      alert("Save failed:\n" + (e?.message || e) + "\n\nIf you keep seeing this, log out and log back in.");
      setSaving(false);
    }
  };

  const saveCashFlow = async () => {
    setSaving(true);
    setError("");

    const { data: authData } = await supabase.auth.getUser();
    const user = authData?.user || null;
    if (!user?.id) {
      throw new Error("Not signed in — refresh the page and log in again before recording a payment.");
    }
    const { data: profile } = await supabase.from("profiles").select("full_name,email").eq("id", user.id).maybeSingle();
    const selectedMethod = PAYMENT_METHODS.find(m => m.id === method);

    const finalDesc = desc || itemsList || ("Payment via " + selectedMethod.label);

    const payment = {
      client_id: client?.id || null,
      client_name: client?.name || null,
      appointment_id: appointmentId || null,
      amount: total,
      status: "succeeded",
      payment_method_type: method,
      payment_method: method,
      description: finalDesc,
      check_number: method === "check" ? checkNumber : null,
      external_reference: externalRef || null,
      created_by_user: user?.id,
      created_by_name: profile?.full_name || profile?.email || "Unknown",
      notes: notes || null,
      metadata: {
        cart_items: cart,
        subtotal: subtotal,
        discount_type: discountType,
        discount_value: +discountValue,
        discount_amount: discountAmount,
      },
    };

    const { error: insertError } = await supabase.from("payments").insert([payment]);
    if (insertError) {
      setError("Failed: " + insertError.message);
      setSaving(false);
      return;
    }

    // ★ If this payment was for a specific existing appointment, mark that
    // appointment as paid + checked-out so it disappears from the Checkout queue
    // and shows everywhere as completed.
    if (appointmentId) {
      try {
        await supabase.from("appointments").update({
          paid: true,
          status: "completed",
          checkin: "checked-out",
          tip: 0,
        }).eq("id", appointmentId);
      } catch (e) { console.warn("Failed to mark appt paid:", e); }
    }

    // ★ Bump the client's lifetime-spent figure so all reports stay in sync.
    if (client?.id) {
      try {
        const { data: current } = await supabase.from("clients").select("spent,visits").eq("id", client.id).single();
        const newSpent = Number(current?.spent || 0) + Number(total || 0);
        const newVisits = Number(current?.visits || 0) + (appointmentId ? 1 : 0); // only bump visits if it was a real appt
        await supabase.from("clients").update({
          spent: newSpent,
          visits: newVisits,
          last: new Date().toISOString().split("T")[0],
        }).eq("id", client.id);
      } catch (e) { console.warn("Failed to update client lifetime spent:", e); }
    }

    // Settle the visit: reuse the client's existing appointment for today (so it
    // stays on their provider's schedule) instead of minting a provider-less
    // ghost appointment. Only creates a row for a true walk-in.
    if (client?.id && cart.length > 0 && !appointmentId) {
      const treatmentName = cart.length === 1 ? cart[0].name : cart.map(i => i.name).join(" + ");
      await settleVisitAppointment({
        clientId: client.id,
        clientName: client.name,
        treatmentName,
        total,
        notes: cart.length > 1 ? "Multi-service: " + cart.map(i => `${i.qty}× ${i.name} ($${i.price})`).join(", ") + (discountAmount > 0 ? ` | Discount: -$${discountAmount.toFixed(2)}` : "") : "",
      });
    }

    // Receipt
    if (client?.email) {
      try {
        await supabase.functions.invoke("send-receipt", { body: {
          to_email: client.email, to_name: client.name || "Client",
          amount: payment.amount, service: finalDesc,
          // Itemized receipt (owner directive): every cart line with its own
          // price, discount separately, total at the end.
          items: cart.map(i => ({ name: i.name, qty: i.qty, price: i.price })),
          subtotal: subtotal,
          discount: discountAmount > 0 ? discountAmount : 0,
          provider: "", date: new Date().toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"}),
          payment_method: (payment.payment_method||"cash").replace(/_/g," "),
          last4: "", transaction_id: payment.external_reference||"",
          notes: payment.notes||"",
        }});
      } catch(e){console.error(e);}
    }

    // ★ AUTO-WALLET — every paid cart item lands in the client's wallet.
    //   Owner directive: "every client who pays — what she bought goes to
    //   her wallet." VISIBLE alert on result so staff knows it happened.
    if (client?.id && cart.length > 0) {
      let walletAdded = 0;
      let firstError = null;
      try {
        const _res = await addCartToWallet(client.id, client.name, cart, {
          paymentId: payment?.id || null,
          paymentMethod: payment?.payment_method || "cash",
          userId: user.id || null,
          userName: profile?.full_name || profile?.email || "Staff",
        });
        walletAdded = _res.added || 0;
        firstError = _res.errors?.[0] || null;
        console.log("[wallet] auto-added", walletAdded, "item(s) to wallet for", client.name, _res);
      } catch (e) {
        firstError = e?.message || String(e);
        console.error("[wallet] auto-add failed:", e);
      }
      if (walletAdded === 0) {
        alert(`⚠️ Payment saved but NO items reached ${client.name}'s wallet.\n\nReason: ${firstError || "Unknown — see console"}\n\nThe payment itself succeeded. You can add items manually via the Wallet "+ Add Item" button.`);
      } else if (firstError) {
        alert(`⚠️ Partial wallet add for ${client.name}: ${walletAdded} of ${cart.length} items added.\n\nFirst failure: ${firstError}`);
      } else {
        console.log(`✅ ${walletAdded} item(s) added to ${client.name}'s wallet`);
      }
    }

    setSuccess(true);
    setSaving(false);
    if (onComplete) onComplete(payment);
  };

  // ── SPLIT PAYMENT ──────────────────────────────────────────────────────────
  // A split sale is recorded as one payments row per tender, all sharing a
  // split_group. The cart is attached to only the FIRST row so the wallet is
  // credited exactly once (mirrors the single-payment path — no double add).
  //
  // A card line in a split must ACTUALLY run on the Stripe terminal — so if a
  // card line exists we open ChargeModal for that amount first; only after the
  // card succeeds do we record the other tenders (commitSplit with
  // cardCharged=true). ChargeModal already bumps client spent by the card
  // amount, so commitSplit then adds only the non-card portion.
  const splitSummaryText = () => splitLines
    .map(l => ({ method: l.method, amount: +(parseFloat(l.amount) || 0) }))
    .filter(l => l.amount > 0)
    .map(l => `$${l.amount.toFixed(2)} ${methodLabel(l.method)}`)
    .join(" + ");

  const commitSplit = async (recordLines, cardCharged) => {
    const { data: authData } = await supabase.auth.getUser();
    const user = authData?.user || null;
    if (!user?.id) throw new Error("Not signed in — refresh the page and log in again.");
    const { data: profile } = await supabase.from("profiles").select("full_name,email").eq("id", user.id).maybeSingle();
    const byName = profile?.full_name || profile?.email || "Unknown";
    const splitGroup = "split_" + Date.now();
    const summary = splitSummaryText();
    const baseDesc = desc || itemsList || "Split payment";
    const nonCardTotal = recordLines.reduce((s, l) => s + l.amount, 0);

    const rows = recordLines.map((l, idx) => ({
      client_id: client?.id || null,
      client_name: client?.name || null,
      appointment_id: appointmentId || null,
      amount: l.amount,
      status: "succeeded",
      payment_method_type: l.method,
      payment_method: l.method,
      description: `${baseDesc} — split: ${methodLabel(l.method)} $${l.amount.toFixed(2)}`,
      check_number: l.method === "check" ? (checkNumber || null) : null,
      external_reference: externalRef || null,
      created_by_user: user.id,
      created_by_name: byName,
      notes: notes || null,
      metadata: {
        split_group: splitGroup,
        split_total: total,
        split_summary: summary,
        card_on_terminal: !!cardCharged,
        // Cart on the FIRST recorded row only → wallet credited once.
        ...(idx === 0
          ? { cart_items: cart, subtotal, discount_type: discountType, discount_value: +discountValue, discount_amount: discountAmount }
          : { cart_items: [] }),
      },
    }));

    if (rows.length > 0) {
      const { error: insErr } = await supabase.from("payments").insert(rows);
      if (insErr) throw new Error(insErr.message);
    }

    // ── Side effects — run ONCE for the whole sale ──
    if (appointmentId) {
      try { await supabase.from("appointments").update({ paid: true, status: "completed", checkin: "checked-out", tip: 0 }).eq("id", appointmentId); }
      catch (e) { console.warn("Failed to mark appt paid:", e); }
    }
    // Bump client spent. When the card ran through ChargeModal it already bumped
    // spent by the card amount, so here we add only the non-card portion.
    if (client?.id) {
      const add = cardCharged ? nonCardTotal : total;
      try {
        const { data: current } = await supabase.from("clients").select("spent,visits").eq("id", client.id).single();
        await supabase.from("clients").update({
          spent: Number(current?.spent || 0) + Number(add || 0),
          visits: Number(current?.visits || 0) + (appointmentId ? 1 : 0),
          last: new Date().toISOString().split("T")[0],
        }).eq("id", client.id);
      } catch (e) { console.warn("Failed to update client lifetime spent:", e); }
    }
    if (client?.id && cart.length > 0 && !appointmentId) {
      const treatmentName = cart.length === 1 ? cart[0].name : cart.map(i => i.name).join(" + ");
      await settleVisitAppointment({
        clientId: client.id,
        clientName: client.name,
        treatmentName,
        total,
        notes: `Split payment: ${summary}` + (cart.length > 1 ? " | " + cart.map(i => `${i.qty}× ${i.name} ($${i.price})`).join(", ") : ""),
      });
    }
    if (client?.email) {
      try {
        await supabase.functions.invoke("send-receipt", { body: {
          to_email: client.email, to_name: client.name || "Client",
          amount: total, service: `${baseDesc} (Split: ${summary})`,
          items: cart.map(i => ({ name: i.name, qty: i.qty, price: i.price })),
          subtotal: subtotal,
          discount: discountAmount > 0 ? discountAmount : 0,
          provider: "", date: new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }),
          payment_method: "Split — " + summary, last4: "", transaction_id: externalRef || "", notes: notes || "",
        }});
      } catch (e) { console.error(e); }
    }
    if (client?.id && cart.length > 0) {
      try {
        const _res = await addCartToWallet(client.id, client.name, cart, {
          paymentId: null, paymentMethod: "split", userId: user.id, userName: byName,
        });
        if ((_res.added || 0) === 0) {
          alert(`⚠️ Payment saved but NO items reached ${client.name}'s wallet.\nReason: ${_res.errors?.[0] || "see console"}`);
        }
      } catch (e) { alert("⚠️ Payment saved but wallet exception: " + (e?.message || e)); }
    }

    setSuccess(true);
    setSaving(false);
    if (onComplete) onComplete(rows[0] || { amount: total });
  };

  const saveSplit = async () => {
    const lines = splitLines
      .map(l => ({ method: l.method, amount: +(parseFloat(l.amount) || 0) }))
      .filter(l => l.amount > 0);
    if (lines.length < 2) { setError("Add at least two payment lines for a split (or use a single method above)."); return; }
    if (total < 0.5) { setError("Total must be at least $0.50."); return; }
    const allocated = lines.reduce((s, l) => s + l.amount, 0);
    if (Math.abs(allocated - total) > 0.01) {
      setError(`Split must add up to $${total.toFixed(2)} — right now it's $${allocated.toFixed(2)} (off by $${Math.abs(allocated - total).toFixed(2)}).`);
      return;
    }
    const cardLines = lines.filter(l => l.method === "card");
    if (cardLines.length > 1) { setError("Use a single Credit Card line in a split — combine the card amount into one line."); return; }
    setError("");

    // Card portion → charge it on the terminal (or saved card / manual) first.
    if (cardLines.length === 1) {
      if (!client?.id) { setError("The credit-card part of a split needs an existing client to charge. Pick the client first, or run the card separately and record it as “Other”."); return; }
      setPendingSplit({ nonCard: lines.filter(l => l.method !== "card"), cardAmount: cardLines[0].amount });
      setShowSplitCard(true);
      return;
    }

    // No card line — record everything now.
    setSaving(true);
    try { await commitSplit(lines, false); }
    catch (e) {
      console.error("[record-payment] split save crashed:", e);
      setError("Save failed: " + (e?.message || e));
      setSaving(false);
    }
  };

  // Called after the card portion succeeds on the terminal — records the rest.
  const finishSplitCard = async () => {
    setShowSplitCard(false);
    setSaving(true);
    try { await commitSplit(pendingSplit?.nonCard || [], true); }
    catch (e) {
      console.error("[record-payment] split finish crashed:", e);
      setError("Card was charged, but recording the other tenders failed: " + (e?.message || e) + " — record them manually.");
      setSaving(false);
    }
    setPendingSplit(null);
  };

  if (success) {
    return (
      <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
        <div style={{background:"#fff",borderRadius:14,width:"100%",maxWidth:400,padding:24,textAlign:"center"}}>
          <div style={{fontSize:48,marginBottom:14}}>✅</div>
          <div style={{fontSize:18,fontWeight:800,color:C.green,fontFamily:S,marginBottom:6}}>Payment Recorded!</div>
          <div style={{fontSize:14,color:C.text,marginBottom:4}}>${total.toFixed(2)} · {PAYMENT_METHODS.find(m=>m.id===method)?.label}</div>
          {client && <div style={{fontSize:12,color:C.muted,marginBottom:8}}>{client.name}</div>}
          {cart.length > 0 && <div style={{fontSize:11,color:C.muted,marginBottom:18,padding:"8px 12px",background:C.light,borderRadius:6}}>{itemsList}</div>}
          <button onClick={onClose} style={{padding:"10px 24px",background:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",color:"#fff",border:"none",borderRadius:8,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:F}}>Done</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",padding:"10px",backdropFilter:"blur(4px)"}}>
      <div style={{background:"#fff",borderRadius:14,width:"100%",maxWidth:540,padding:"clamp(14px, 4vw, 24px)",maxHeight:"94vh",overflowY:"auto",position:"relative"}}>
        <button onClick={onClose} style={{position:"absolute",top:12,right:12,background:C.light,border:"none",borderRadius:7,padding:"4px 10px",cursor:"pointer",fontSize:16,color:C.muted,zIndex:10}}>×</button>

        <div style={{fontSize:18,fontWeight:800,color:C.gold,fontFamily:S,marginBottom:4}}>📋 Record Payment</div>
        {client && <div style={{fontSize:12,color:C.muted,marginBottom:16}}>{client.name}</div>}

        {/* Toggle */}
        <div style={{display:"flex",gap:6,marginBottom:14,padding:3,background:C.light,borderRadius:8}}>
          <button onClick={()=>setUseCustom(false)} style={{flex:1,padding:"7px 10px",background:!useCustom?"#fff":"transparent",color:!useCustom?C.gold:C.muted,border:"none",borderRadius:6,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:F,boxShadow:!useCustom?"0 1px 3px rgba(0,0,0,0.06)":"none"}}>🛒 Pick Services</button>
          <button onClick={()=>setUseCustom(true)} style={{flex:1,padding:"7px 10px",background:useCustom?"#fff":"transparent",color:useCustom?C.gold:C.muted,border:"none",borderRadius:6,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:F,boxShadow:useCustom?"0 1px 3px rgba(0,0,0,0.06)":"none"}}>💰 Custom Amount</button>
        </div>

        {!useCustom && (
          <div style={{marginBottom:14}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
              <label style={{fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase"}}>Services ({cart.length})</label>
              <button onClick={()=>setShowServicePicker(true)} style={{padding:"5px 12px",background:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",color:"#fff",border:"none",borderRadius:6,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:F}}>+ Add Service</button>
            </div>
            {cart.length === 0 ? (
              <div style={{padding:18,textAlign:"center",background:C.light,border:"1px dashed "+C.border,borderRadius:8,color:C.muted,fontSize:12}}>No services selected. Click + Add Service.</div>
            ) : (
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                {cart.map(item => (
                  <div key={item.id} style={{display:"flex",alignItems:"center",gap:8,padding:"9px 12px",background:C.goldBg,border:"1px solid "+C.borderGold,borderRadius:7}}>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:13,fontWeight:600,color:C.text}}>{item.name}</div>
                      <div style={{display:"flex",alignItems:"center",gap:4,marginTop:3}}>
                        <span style={{fontSize:11,color:C.muted}}>$</span>
                        <input type="number" step="0.01" min="0" inputMode="decimal" value={item.price}
                          onChange={e=>{ const v=parseFloat(e.target.value); updatePrice(item.id, Number.isNaN(v)?0:v); }}
                          onFocus={e=>e.target.select()}
                          style={{width:74,fontSize:12,fontWeight:600,textAlign:"right",border:"1px solid "+C.border,borderRadius:6,padding:"4px 6px",background:"#fff",color:C.text,fontFamily:F,outline:"none"}}/>
                        <span style={{fontSize:11,color:C.muted}}>each</span>
                      </div>
                    </div>
                    <div style={{display:"flex",alignItems:"center",gap:6}}>
                      <button onClick={()=>updateQty(item.id, item.qty - 1)} style={{minWidth:36,minHeight:36,background:"#fff",border:"1px solid "+C.border,borderRadius:8,cursor:"pointer",fontSize:18,color:C.muted,touchAction:"manipulation"}}>−</button>
                      <input
                        type="number"
                        inputMode="numeric"
                        min="1"
                        value={item.qty}
                        onChange={e => {
                          const n = parseInt(e.target.value, 10);
                          if (Number.isNaN(n)) return; // ignore garbage
                          updateQty(item.id, Math.max(1, n));
                        }}
                        onFocus={e => e.target.select()}
                        style={{width:54,minHeight:36,textAlign:"center",fontSize:15,fontWeight:700,border:"1px solid "+C.border,borderRadius:8,padding:"4px 6px",background:"#fff",color:C.text,fontFamily:F,outline:"none"}}
                      />
                      <button onClick={()=>updateQty(item.id, item.qty + 1)} style={{minWidth:36,minHeight:36,background:"#fff",border:"1px solid "+C.border,borderRadius:8,cursor:"pointer",fontSize:18,color:C.muted,touchAction:"manipulation"}}>+</button>
                    </div>
                    <div style={{minWidth:60,textAlign:"right",fontSize:13,fontWeight:700,color:C.gold}}>${(item.price * item.qty).toFixed(2)}</div>
                    <button onClick={()=>removeFromCart(item.id)} style={{background:"none",border:"none",color:C.red,cursor:"pointer",fontSize:14,padding:"0 4px"}}>×</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {useCustom && (
          <div style={{marginBottom:14}}>
            <label style={{fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase",display:"block",marginBottom:6}}>Custom Amount ($)</label>
            <input type="number" step="0.01" value={customAmount} onChange={e=>setCustomAmount(e.target.value)} placeholder="100.00" style={{...inp,fontSize:18,fontWeight:700}} autoFocus/>
            <input value={desc} onChange={e=>setDesc(e.target.value)} placeholder="Description (optional)" style={{...inp,marginTop:8,fontSize:13}}/>
          </div>
        )}

        {/* Discount */}
        {(cart.length > 0 || useCustom) && subtotal > 0 && (
          <div style={{marginBottom:14,padding:12,background:"rgba(46,139,87,0.06)",border:"1px solid "+C.green+"30",borderRadius:8}}>
            <label style={{fontSize:11,fontWeight:700,color:C.green,textTransform:"uppercase",display:"block",marginBottom:6}}>🏷 Discount (optional)</label>
            <div style={{display:"flex",gap:6}}>
              <select value={discountType} onChange={e=>setDiscountType(e.target.value)} style={{padding:"8px 10px",background:"#fff",border:"1px solid "+C.border,borderRadius:7,fontSize:12,fontFamily:F}}>
                <option value="amount">$ off</option>
                <option value="percent">% off</option>
              </select>
              <input type="number" step="0.01" min="0" value={discountValue} onChange={e=>setDiscountValue(e.target.value)} placeholder="0" style={{flex:1,padding:"8px 12px",background:"#fff",border:"1px solid "+C.border,borderRadius:7,fontSize:13,fontWeight:600,fontFamily:F,outline:"none",boxSizing:"border-box"}}/>
            </div>
            {discountValue > 0 && (
              <div style={{fontSize:11,color:C.green,marginTop:5,fontWeight:600}}>
                Saving ${discountAmount.toFixed(2)} {discountType === "percent" ? `(${discountValue}% off)` : ""}
              </div>
            )}
          </div>
        )}

        {/* Total */}
        {(cart.length > 0 || useCustom) && (
          <div style={{padding:14,background:C.light,borderRadius:10,marginBottom:14,border:"1px solid "+C.border}}>
            {(discountValue > 0 && discountAmount > 0) && (
              <>
                <div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:C.muted,marginBottom:4}}><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
                <div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:C.green,marginBottom:6}}><span>Discount</span><span>−${discountAmount.toFixed(2)}</span></div>
              </>
            )}
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",paddingTop:6,borderTop:"1px solid "+C.border}}>
              <span style={{fontSize:13,fontWeight:700,color:C.text}}>Total</span>
              <span style={{fontSize:24,fontWeight:800,color:C.gold,fontFamily:S}}>${total.toFixed(2)}</span>
            </div>
          </div>
        )}

        {/* Payment method — auto-fit grid keeps tiles ≥120px so they stay
            tappable on iPhone (2 cols at 390px), expand to 3 on iPad,
            up to whatever fits on desktop. */}
        <div style={{marginBottom:14}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8,gap:8}}>
            <label style={{fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase"}}>Payment Method</label>
            {total >= 0.5 && (
              <button onClick={()=>{ setError(""); setSplitMode(s=>{ const n=!s; if(n) setSplitLines([{method:"cash",amount:""},{method:"card",amount:""}]); return n; }); }}
                style={{padding:"5px 11px",background:splitMode?C.gold:"#fff",color:splitMode?"#fff":C.gold,border:"1px solid "+(splitMode?C.gold:C.borderGold||"rgba(180,140,60,0.3)"),borderRadius:99,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:F,whiteSpace:"nowrap"}}>
                {splitMode ? "◀ Single payment" : "🧩 Split payment"}
              </button>
            )}
          </div>

          {!splitMode && (
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(110px, 1fr))",gap:8}}>
              {PAYMENT_METHODS.map(m => (
                <button key={m.id} onClick={()=>setMethod(m.id)} style={{minHeight:64,padding:"10px 8px",border:"2px solid "+(method===m.id?m.color:C.border),borderRadius:8,cursor:"pointer",background:method===m.id?m.color+"15":"#fff",fontFamily:F,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:4,transition:"all 0.15s",touchAction:"manipulation"}}>
                  <span style={{fontSize:22}}>{m.icon}</span>
                  <span style={{fontSize:12,fontWeight:method===m.id?700:500,color:method===m.id?m.color:C.text}}>{m.label}</span>
                </button>
              ))}
            </div>
          )}

          {/* Split editor — one {method, amount} line per tender; must total up. */}
          {splitMode && (
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {splitLines.map((l,i)=>(
                <div key={i} style={{display:"flex",gap:6,alignItems:"center"}}>
                  <select value={l.method} onChange={e=>setSplitLines(ls=>ls.map((x,j)=>j===i?{...x,method:e.target.value}:x))} style={{flex:"0 0 132px",padding:"10px 8px",border:"1px solid "+C.border,borderRadius:8,fontSize:13,fontFamily:F,background:"#fff",color:C.text}}>
                    {PAYMENT_METHODS.filter(m=>m.id!=="other").map(m=><option key={m.id} value={m.id}>{m.icon} {m.label}</option>)}
                  </select>
                  <div style={{position:"relative",flex:1}}>
                    <span style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",color:C.muted,fontSize:14}}>$</span>
                    <input type="number" step="0.01" min="0" inputMode="decimal" value={l.amount} onChange={e=>setSplitLines(ls=>ls.map((x,j)=>j===i?{...x,amount:e.target.value}:x))} onFocus={e=>e.target.select()} placeholder="0.00" style={{width:"100%",padding:"10px 10px 10px 22px",border:"1px solid "+C.border,borderRadius:8,fontSize:15,fontWeight:700,fontFamily:F,boxSizing:"border-box",color:C.text}}/>
                  </div>
                  <button onClick={()=>{ const others=splitLines.reduce((s,x,j)=>j===i?s:s+(parseFloat(x.amount)||0),0); const rest=Math.max(0,+(total-others).toFixed(2)); setSplitLines(ls=>ls.map((x,j)=>j===i?{...x,amount:String(rest)}:x)); }} title="Fill the remaining balance" style={{padding:"9px 8px",background:C.light,border:"1px solid "+C.border,borderRadius:7,fontSize:10,fontWeight:700,cursor:"pointer",color:C.gold,fontFamily:F,whiteSpace:"nowrap"}}>= rest</button>
                  {splitLines.length>2 && <button onClick={()=>setSplitLines(ls=>ls.filter((_,j)=>j!==i))} title="Remove line" style={{background:"none",border:"none",color:C.red,cursor:"pointer",fontSize:18,padding:"0 2px"}}>×</button>}
                </div>
              ))}
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:8}}>
                <button onClick={()=>setSplitLines(ls=>[...ls,{method:"cash",amount:""}])} style={{padding:"7px 12px",background:"#fff",border:"1px dashed "+(C.borderGold||"rgba(180,140,60,0.35)"),borderRadius:7,fontSize:11,fontWeight:700,color:C.gold,cursor:"pointer",fontFamily:F}}>+ Add payment</button>
                <div style={{fontSize:12.5,fontWeight:800,color:splitBalanced?C.green:C.red}}>
                  {splitBalanced ? "✓ Balanced" : splitRemaining>0 ? `Remaining $${splitRemaining.toFixed(2)}` : `Over by $${Math.abs(splitRemaining).toFixed(2)}`}
                </div>
              </div>
              <div style={{fontSize:11,color:C.muted,lineHeight:1.5}}>Allocated <strong>${splitAllocated.toFixed(2)}</strong> of <strong>${total.toFixed(2)}</strong>. 🍒 Cherry / CareCredit / Affirm are financing (recorded). 💳 If a line is Credit Card, pressing Record opens the terminal to charge that amount first, then records the rest.</div>
            </div>
          )}

          {/* ★ Card processing-fee preview (single-payment card routes to Stripe). */}
          {!splitMode && method === "card" && paySettings?.cc_fee_enabled && cardFeePct(false, paySettings) > 0 && (
            <div style={{marginTop:10,padding:"10px 12px",background:C.goldBg||"rgba(180,140,60,0.08)",border:"1px solid "+(C.borderGold||"rgba(180,140,60,0.3)"),borderRadius:8,fontSize:12,color:C.text,lineHeight:1.5}}>
              💳 A <strong>{feeLabel(paySettings)}</strong> is added on the next (secure card) screen:
              <div style={{marginTop:4}}>
                • Terminal (tap/insert): +{cardFeePct(true,paySettings)}% → <strong>${(total+cardFeeFor(total,true,paySettings)).toFixed(2)}</strong><br/>
                • Typed by hand: +{cardFeePct(false,paySettings)}% → <strong>${(total+cardFeeFor(total,false,paySettings)).toFixed(2)}</strong>
              </div>
            </div>
          )}
        </div>

        {!splitMode && method === "check" && (
          <div style={{marginBottom:14}}>
            <label style={{fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase",display:"block",marginBottom:6}}>Check Number</label>
            <input value={checkNumber} onChange={e=>setCheckNumber(e.target.value)} placeholder="e.g. 1234" style={inp}/>
          </div>
        )}

        {!splitMode && (method === "cherry" || method === "carecredit" || method === "affirm" || method === "zelle" || method === "venmo") && (
          <div style={{marginBottom:14}}>
            <label style={{fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase",display:"block",marginBottom:6}}>Reference / Confirmation # (optional)</label>
            <input value={externalRef} onChange={e=>setExternalRef(e.target.value)} placeholder="Approval code, transaction ID..." style={inp}/>
          </div>
        )}

        <div style={{marginBottom:18}}>
          <label style={{fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase",display:"block",marginBottom:6}}>Notes (optional)</label>
          <textarea value={notes} onChange={e=>setNotes(e.target.value)} rows={2} placeholder="Any additional notes..." style={{...inp,resize:"vertical"}}/>
        </div>

        {error && <div style={{padding:"10px 14px",background:C.redL,color:C.red,borderRadius:8,fontSize:12,marginBottom:12,fontWeight:600}}>{error}</div>}

        <div style={{display:"flex",gap:8}}>
          <button onClick={onClose} style={{flex:1,padding:"11px",background:C.light,color:C.muted,border:"1px solid "+C.border,borderRadius:8,fontSize:13,cursor:"pointer",fontFamily:F}}>Cancel</button>
          {(() => {
            const disabled = saving || total < 0.5 || (splitMode && !splitBalanced);
            return (
              <button onClick={splitMode ? saveSplit : save} disabled={disabled} style={{flex:2,padding:"11px",background:disabled?C.light:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",color:disabled?C.muted:"#fff",border:"none",borderRadius:8,fontSize:13,fontWeight:700,cursor:disabled?"not-allowed":"pointer",fontFamily:F}}>{saving?"Saving...":splitMode?(splitHasCard?`💳 Charge card + record split $${total.toFixed(2)}`:`Record split $${total.toFixed(2)}`):`Record $${total.toFixed(2)}`}</button>
            );
          })()}
        </div>
      </div>

      {showServicePicker && (
        <div onClick={()=>setShowServicePicker(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.4)",zIndex:3000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
          <div onClick={e=>e.stopPropagation()} style={{background:"#fff",borderRadius:12,width:"100%",maxWidth:420,maxHeight:"80vh",display:"flex",flexDirection:"column",overflow:"hidden"}}>
            <div style={{padding:14,borderBottom:"1px solid "+C.border,background:C.goldBg}}>
              <div style={{display:"flex",gap:6,marginBottom:10}}>
                <button onClick={()=>setPickerTab("service")} style={{flex:1,padding:"8px 12px",background:pickerTab==="service"?"linear-gradient(135deg,"+C.gold+","+C.goldD+")":"#fff",color:pickerTab==="service"?"#fff":C.text,border:"1px solid "+(pickerTab==="service"?C.gold:C.border),borderRadius:7,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:F}}>
                  ✨ Services ({serviceCount})
                </button>
                <button onClick={()=>setPickerTab("product")} style={{flex:1,padding:"8px 12px",background:pickerTab==="product"?"linear-gradient(135deg,"+C.gold+","+C.goldD+")":"#fff",color:pickerTab==="product"?"#fff":C.text,border:"1px solid "+(pickerTab==="product"?C.gold:C.border),borderRadius:7,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:F}}>
                  🛍 Products ({productCount})
                </button>
              </div>
              <input autoFocus value={serviceSearch} onChange={e=>setServiceSearch(e.target.value)} placeholder={pickerTab==="product"?"🔍 Search products...":"🔍 Search services..."} style={{width:"100%",padding:"10px 14px",background:"#fff",border:"1px solid "+C.border,borderRadius:7,fontSize:13,fontFamily:F,outline:"none",boxSizing:"border-box"}}/>
            </div>
            <div style={{flex:1,overflow:"auto"}}>
              {filteredServices.length === 0 ? (
                <div style={{padding:30,textAlign:"center",color:C.muted,fontSize:12}}>No matches</div>
              ) : filteredServices.map(s => (
                <button key={s.id} onClick={()=>addToCart(s)} style={{width:"100%",padding:"10px 14px",border:"none",borderBottom:"1px solid "+C.border,background:"#fff",cursor:"pointer",textAlign:"left",fontFamily:F,display:"flex",justifyContent:"space-between",alignItems:"center"}}
                  onMouseEnter={e=>e.currentTarget.style.background=C.goldBg}
                  onMouseLeave={e=>e.currentTarget.style.background="#fff"}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:13,fontWeight:600,color:C.text}}>{s.name}</div>
                    <div style={{fontSize:10,color:C.muted}}>{s.category}</div>
                  </div>
                  <div style={{fontSize:13,fontWeight:700,color:C.gold,flexShrink:0,marginLeft:10}}>${(+s.price||0).toFixed(0)}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {showStripeCharge && (
        <ChargeModal
          client={client}
          defaultAmount={+total}
          description={desc || itemsList}
          appointmentId={appointmentId}
          onClose={()=>setShowStripeCharge(false)}
          onComplete={(p)=>{setShowStripeCharge(false);setSuccess(true);if(onComplete)onComplete(p);}}
        />
      )}

      {/* Split card portion — charge just the card amount on the terminal /
          saved card / manual. We pass the client WITHOUT email so ChargeModal
          doesn't send its own receipt (commitSplit sends one for the whole
          sale) and no appointmentId so it doesn't touch the appointment; the
          card row it records + its own spent bump cover the card portion, and
          finishSplitCard records the remaining tenders + wallet. */}
      {showSplitCard && pendingSplit && (
        <ChargeModal
          client={client ? { ...client, email: null } : client}
          defaultAmount={+pendingSplit.cardAmount}
          description={`${desc || itemsList || "Payment"} — split card portion ($${Number(pendingSplit.cardAmount).toFixed(2)})`}
          onClose={()=>{ setShowSplitCard(false); setPendingSplit(null); setSaving(false); }}
          onComplete={()=>finishSplitCard()}
        />
      )}
    </div>
  );
}

export function SalesReport() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterMethod, setFilterMethod] = useState("all");
  const [filterPeriod, setFilterPeriod] = useState("today");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");

  useEffect(() => {
    load();
    const ch = supabase.channel("sales-report")
      .on("postgres_changes", { event: "*", schema: "public", table: "payments" }, () => load())
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, []);

  const load = async () => {
    const { data } = await supabase.from("payments").select("*").order("created_at", { ascending: false });
    setPayments(data || []);
    setLoading(false);
  };

  const getDateRange = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (filterPeriod === "today") return { from: today, to: tomorrow };
    if (filterPeriod === "yesterday") {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      return { from: yesterday, to: today };
    }
    if (filterPeriod === "week") {
      const weekAgo = new Date(today);
      weekAgo.setDate(weekAgo.getDate() - 7);
      return { from: weekAgo, to: tomorrow };
    }
    if (filterPeriod === "month") {
      const monthAgo = new Date(today);
      monthAgo.setDate(monthAgo.getDate() - 30);
      return { from: monthAgo, to: tomorrow };
    }
    if (filterPeriod === "year") {
      const yearAgo = new Date(today);
      yearAgo.setFullYear(yearAgo.getFullYear() - 1);
      return { from: yearAgo, to: tomorrow };
    }
    if (filterPeriod === "custom" && filterFrom && filterTo) {
      return { from: new Date(filterFrom), to: new Date(new Date(filterTo).getTime() + 86400000) };
    }
    return { from: new Date(0), to: new Date(2100,0,1) };
  };

  const filtered = payments.filter(p => {
    if (p.refunded) return false;
    if (p.status !== "succeeded") return false;
    if (filterMethod !== "all" && p.payment_method !== filterMethod) return false;
    const created = new Date(p.created_at);
    const { from, to } = getDateRange();
    if (created < from || created >= to) return false;
    return true;
  });

  const total = filtered.reduce((s,p) => s + (+p.amount), 0);
  const byMethod = {};
  PAYMENT_METHODS.forEach(m => {
    const methodPayments = filtered.filter(p => p.payment_method === m.id);
    byMethod[m.id] = {
      count: methodPayments.length,
      total: methodPayments.reduce((s,p) => s + (+p.amount), 0),
      ...m
    };
  });

  const refunded = payments.filter(p => p.refunded).reduce((s,p) => s + (+p.refund_amount || +p.amount), 0);

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20,flexWrap:"wrap",gap:10}}>
        <div>
          <div style={{fontSize:22,fontWeight:800,color:C.gold,fontFamily:S}}>Sales Report</div>
          <div style={{fontSize:12,color:C.muted,marginTop:2}}>Track revenue across all payment methods</div>
        </div>
      </div>

      <div style={{display:"flex",gap:8,marginBottom:20,flexWrap:"wrap"}}>
        {[["today","Today"],["yesterday","Yesterday"],["week","Last 7 Days"],["month","Last 30 Days"],["year","Last Year"],["all","All Time"],["custom","Custom"]].map(([v,l])=>(
          <button key={v} onClick={()=>setFilterPeriod(v)} style={{padding:"8px 14px",background:filterPeriod===v?"linear-gradient(135deg,"+C.gold+","+C.goldD+")":C.light,color:filterPeriod===v?"#fff":C.text,border:"1px solid "+(filterPeriod===v?C.gold:C.border),borderRadius:7,fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:F}}>{l}</button>
        ))}
      </div>

      {filterPeriod === "custom" && (
        <div style={{display:"flex",gap:10,marginBottom:20}}>
          <div style={{flex:1}}>
            <label style={{fontSize:10,fontWeight:700,color:C.gold,textTransform:"uppercase",display:"block",marginBottom:4}}>From</label>
            <input type="date" value={filterFrom} onChange={e=>setFilterFrom(e.target.value)} style={inp} />
          </div>
          <div style={{flex:1}}>
            <label style={{fontSize:10,fontWeight:700,color:C.gold,textTransform:"uppercase",display:"block",marginBottom:4}}>To</label>
            <input type="date" value={filterTo} onChange={e=>setFilterTo(e.target.value)} style={inp} />
          </div>
        </div>
      )}

      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))",gap:12,marginBottom:20}}>
        <div style={{padding:20,background:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",color:"#fff",borderRadius:12}}>
          <div style={{fontSize:11,opacity:0.9,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Total Revenue</div>
          <div style={{fontSize:28,fontWeight:800,fontFamily:S}}>${total.toFixed(2)}</div>
          <div style={{fontSize:11,opacity:0.9,marginTop:2}}>{filtered.length} transactions</div>
        </div>
        <div style={{padding:20,background:C.green+"15",border:"1px solid "+C.green+"40",borderRadius:12}}>
          <div style={{fontSize:11,color:C.green,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4,fontWeight:700}}>Average Transaction</div>
          <div style={{fontSize:22,fontWeight:800,color:C.text,fontFamily:S}}>${filtered.length > 0 ? (total / filtered.length).toFixed(2) : "0.00"}</div>
        </div>
        {refunded > 0 && (
          <div style={{padding:20,background:C.redL,border:"1px solid "+C.red+"40",borderRadius:12}}>
            <div style={{fontSize:11,color:C.red,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4,fontWeight:700}}>Total Refunded</div>
            <div style={{fontSize:22,fontWeight:800,color:C.red,fontFamily:S}}>${refunded.toFixed(2)}</div>
          </div>
        )}
      </div>

      <div style={{marginBottom:20}}>
        <div style={{fontSize:13,fontWeight:700,color:C.gold,fontFamily:S,marginBottom:10}}>Breakdown by Payment Method</div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))",gap:8}}>
          {PAYMENT_METHODS.map(m => {
            const data = byMethod[m.id];
            if (!data || data.count === 0) return null;
            const pct = total > 0 ? (data.total / total * 100).toFixed(1) : 0;
            return (
              <div key={m.id} onClick={()=>setFilterMethod(filterMethod===m.id?"all":m.id)} style={{padding:14,background:"#fff",border:"1px solid "+(filterMethod===m.id?m.color:C.border),borderLeft:"4px solid "+m.color,borderRadius:10,cursor:"pointer"}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                  <span style={{fontSize:20}}>{m.icon}</span>
                  <div style={{fontSize:12,fontWeight:700,color:C.text}}>{m.label}</div>
                </div>
                <div style={{fontSize:18,fontWeight:800,color:m.color}}>${data.total.toFixed(2)}</div>
                <div style={{fontSize:10,color:C.muted,marginTop:2}}>{data.count} transactions · {pct}%</div>
              </div>
            );
          })}
        </div>
      </div>

      <div style={{marginBottom:10,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div style={{fontSize:13,fontWeight:700,color:C.gold,fontFamily:S}}>
          Transactions {filterMethod !== "all" ? "· " + PAYMENT_METHODS.find(m=>m.id===filterMethod)?.label : ""}
        </div>
        {filterMethod !== "all" && <button onClick={()=>setFilterMethod("all")} style={{padding:"5px 10px",background:C.light,color:C.muted,border:"1px solid "+C.border,borderRadius:6,fontSize:11,cursor:"pointer",fontFamily:F}}>Clear filter</button>}
      </div>

      {loading && <div style={{padding:30,textAlign:"center",color:C.muted}}>Loading...</div>}
      
      {!loading && filtered.length === 0 && (
        <div style={{padding:40,textAlign:"center",background:C.light,borderRadius:10,color:C.muted,fontSize:12,border:"1px dashed "+C.border}}>No transactions for this period</div>
      )}

      <div style={{background:"#fff",border:"1px solid "+C.border,borderRadius:10,overflow:"hidden"}}>
        {filtered.map((p, idx) => {
          const m = PAYMENT_METHODS.find(x => x.id === p.payment_method) || PAYMENT_METHODS[0];
          return (
            <div key={p.id} style={{padding:"12px 16px",borderBottom:idx < filtered.length - 1 ? "1px solid "+C.border : "none",display:"flex",alignItems:"center",gap:12}}>
              <div style={{width:36,height:36,borderRadius:9,background:m.color+"20",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>{m.icon}</div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13,fontWeight:700,color:C.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                  {p.client_name || "Walk-in"} {p.description ? "· " + p.description : ""}
                </div>
                <div style={{fontSize:10,color:C.muted,marginTop:2}}>
                  {new Date(p.created_at).toLocaleString("en-US",{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"})}
                  {p.created_by_name && " · by "+p.created_by_name.split(" ")[0]}
                  {p.check_number && " · Check #"+p.check_number}
                  {p.external_reference && " · Ref: "+p.external_reference}
                  {p.card_last4 && " · ••••"+p.card_last4}
                </div>
              </div>
              <div style={{textAlign:"right"}}>
                <div style={{fontSize:15,fontWeight:800,color:m.color}}>${(+p.amount).toFixed(2)}</div>
                <div style={{fontSize:9,color:C.muted,textTransform:"uppercase",marginTop:1}}>{m.label}</div>
              </div>
              {p.client_id && (
                <button onClick={async()=>{
                  const { data: client } = await supabase.from("clients").select("*").eq("id",p.client_id).maybeSingle();
                  if(!client?.email){alert("No email on file for this client");return;}
                  await sendReceipt(p, client);
                }} title="Send email receipt" style={{padding:"8px 10px",background:"rgba(37,99,235,0.08)",color:"#2563eb",border:"1px solid rgba(37,99,235,0.3)",borderRadius:7,fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:F,whiteSpace:"nowrap",flexShrink:0}}>📧 Receipt</button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function TakePaymentPage() {
  const [mode, setMode] = useState("select");
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [guestName, setGuestName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [search, setSearch] = useState("");
  const [showPayModal, setShowPayModal] = useState(false);

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    // Supabase REST defaults to 1000 rows. Nova has 2500+ clients, so the
    // un-paginated query was silently dropping everyone past "M" — that's
    // why "or atarzada" (and anyone alphabetically late) couldn't be found
    // in the Take Payment search. Pull in pages until we get them all.
    const all = [];
    let from = 0;
    const PAGE = 1000;
    while (true) {
      const { data } = await supabase.from("clients").select("id,name,email,phone").order("name").range(from, from + PAGE - 1);
      if (!data || data.length === 0) break;
      all.push(...data);
      if (data.length < PAGE) break;
      from += PAGE;
    }
    setClients(all);
  };

  const startClientPayment = (c) => {
    setSelectedClient(c);
    setShowPayModal(true);
  };

  const startGuestPayment = () => {
    if (!guestName.trim()) {
      alert("Enter a guest name first");
      return;
    }
    setSelectedClient({ id: null, name: guestName.trim(), email: guestEmail.trim() });
    setShowPayModal(true);
  };

  const handleComplete = () => {
    setShowPayModal(false);
    setSelectedClient(null);
    setGuestName("");
    setMode("select");
  };

  const filteredClients = clients.filter(c => 
    c.name?.toLowerCase().includes(search.toLowerCase()) ||
    c.email?.toLowerCase().includes(search.toLowerCase()) ||
    c.phone?.includes(search)
  );

  return (
    <div>
      <div style={{marginBottom:20}}>
        <div style={{fontSize:22,fontWeight:800,color:C.gold,fontFamily:S}}>💰 Take Payment</div>
        <div style={{fontSize:12,color:C.muted,marginTop:2}}>Accept payment from a client or guest</div>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:20}}>
        <button onClick={()=>setMode("client")} style={{padding:"20px 16px",background:mode==="client"?"linear-gradient(135deg,"+C.gold+","+C.goldD+")":"#fff",color:mode==="client"?"#fff":C.text,border:"2px solid "+(mode==="client"?C.gold:C.border),borderRadius:12,cursor:"pointer",fontFamily:F,textAlign:"left",transition:"all 0.15s"}}>
          <div style={{fontSize:24,marginBottom:6}}>👤</div>
          <div style={{fontSize:14,fontWeight:800,marginBottom:3}}>Existing Client</div>
          <div style={{fontSize:11,opacity:0.8}}>Select from your clients</div>
        </button>
        <button onClick={()=>setMode("guest")} style={{padding:"20px 16px",background:mode==="guest"?"linear-gradient(135deg,"+C.gold+","+C.goldD+")":"#fff",color:mode==="guest"?"#fff":C.text,border:"2px solid "+(mode==="guest"?C.gold:C.border),borderRadius:12,cursor:"pointer",fontFamily:F,textAlign:"left",transition:"all 0.15s"}}>
          <div style={{fontSize:24,marginBottom:6}}>🚶</div>
          <div style={{fontSize:14,fontWeight:800,marginBottom:3}}>Guest / Walk-in</div>
          <div style={{fontSize:11,opacity:0.8}}>No account needed</div>
        </button>
      </div>

      {mode === "client" && (
        <div>
          <input 
            value={search} 
            onChange={e=>setSearch(e.target.value)}
            placeholder="Search by name, email, or phone..." 
            style={{...inp,marginBottom:12,fontSize:14,padding:"12px 14px"}}
            autoFocus
          />
          <div style={{maxHeight:400,overflowY:"auto",background:"#fff",border:"1px solid "+C.border,borderRadius:10}}>
            {filteredClients.length === 0 && (
              <div style={{padding:30,textAlign:"center",color:C.muted,fontSize:12}}>
                {search ? "No clients match your search" : "No clients yet"}
              </div>
            )}
            {filteredClients.map((c, idx) => (
              <div key={c.id} onClick={()=>startClientPayment(c)} style={{padding:"12px 14px",borderBottom:idx < filteredClients.length - 1 ? "1px solid "+C.border : "none",cursor:"pointer",display:"flex",alignItems:"center",gap:12,transition:"background 0.1s"}} onMouseEnter={e=>e.currentTarget.style.background=C.goldBg} onMouseLeave={e=>e.currentTarget.style.background="#fff"}>
                <div style={{width:36,height:36,borderRadius:9,background:C.goldBg,border:"1px solid "+C.borderGold,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:700,color:C.gold,flexShrink:0}}>{c.name?.split(" ").map(n=>n[0]).join("").slice(0,2).toUpperCase()}</div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:13,fontWeight:700,color:C.text}}>{c.name}</div>
                  <div style={{fontSize:11,color:C.muted,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.email || c.phone || ""}</div>
                </div>
                <div style={{fontSize:11,color:C.gold,fontWeight:600}}>→</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {mode === "guest" && (
        <div style={{background:"#fff",border:"1px solid "+C.border,borderRadius:10,padding:20}}>
          <div style={{fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase",marginBottom:6}}>Guest Name</div>
          <input 
            value={guestName} 
            onChange={e=>setGuestName(e.target.value)}
            placeholder="e.g. Jane Smith" 
            style={{...inp,fontSize:14,padding:"12px 14px",marginBottom:12}}
            autoFocus
          />
          <div style={{fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase",marginBottom:6}}>📧 Email for Receipt (optional)</div>
          <input
            type="email"
            value={guestEmail}
            onChange={e=>setGuestEmail(e.target.value)}
            placeholder="e.g. jane@email.com"
            style={{...inp,fontSize:14,padding:"12px 14px",marginBottom:6}}
          />
          <div style={{fontSize:10,color:C.muted,fontStyle:"italic",marginBottom:12,paddingLeft:2}}>
            💡 Add email to automatically send a receipt after payment
          </div>
                    <button onClick={startGuestPayment} disabled={!guestName.trim()} style={{width:"100%",padding:"12px",background:!guestName.trim()?C.light:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",color:!guestName.trim()?C.muted:"#fff",border:"none",borderRadius:8,fontSize:13,fontWeight:700,cursor:!guestName.trim()?"not-allowed":"pointer",fontFamily:F}}>Continue to Payment →</button>
        </div>
      )}

      {showPayModal && selectedClient && (
        <RecordPaymentModal 
          client={selectedClient} 
          onClose={()=>{setShowPayModal(false);setSelectedClient(null);}}
          onComplete={handleComplete}
        />
      )}
    </div>
  );
}